import { useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  ChakraTab,
  ChakraTabList,
  ChakraTabPanel,
  ChakraTabPanels,
  ChakraTabs,
  ChakraText,
  Close,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  IconButton,
} from '@equip.health/ui';

import { SurveyNameToDisplayNameMap } from '~/lib/constants/survey/SurveyNameToDisplayNameMap';
import { SurveyInstanceData } from '~/lib/types/assessments/SurveyInstanceData';

import AssessmentsTable from './AssessmentsTable';

type AssessmentsViewProps = {
  closeModal: () => void;
  id: string;
  tableData: TableData;
};

export type TableData = {
  clickedSurveyData: SurveyInstanceData;
  lastFiveSurveyData: SurveyInstanceData[];
  surveyIndex: number;
};

const { label: labelStyle } = EquipUITheme.typography;
const { neutral } = EquipUIFireflyV2Theme.colors;

const AssessmentsView = ({
  closeModal,
  id,
  tableData,
}: AssessmentsViewProps) => {
  const [tabIndex, setTabIndex] = useState(0);

  const { clickedSurveyData, lastFiveSurveyData, surveyIndex } = tableData;
  const { surveyName } = clickedSurveyData;
  const title = SurveyNameToDisplayNameMap[surveyName];

  return (
    <ChakraBox
      borderLeft={`1px solid ${neutral.line}`}
      flexGrow={1}
      id={`${id}__container`}
    >
      <ChakraTabs
        id={`${id}__tabs`}
        index={tabIndex}
        isLazy
        onChange={(index) => {
          setTabIndex(index);
        }}
        variant="soft-rounded"
      >
        <ChakraFlex
          alignItems="center"
          borderBottom={`1px solid ${neutral.line}`}
          id={`${id}__nav-bar`}
          justifyContent="space-between"
          padding="24px"
        >
          <ChakraText fontSize="20px" fontWeight="bold" id={`${id}__title`}>
            {`${title} (ID number ${surveyIndex})`}
          </ChakraText>
          <ChakraHStack id={`${id}__tabs-container`} spacing="10px">
            <ChakraText id={`${id}__tabs-label`} {...labelStyle}>
              View mode
            </ChakraText>

            <ChakraTabList
              background="neutral.background.secondary"
              borderRadius="29px"
              id={`${id}__tabs-list`}
              width="fit-content"
            >
              {['Only 1', 'Last 5'].map((eachTab) => (
                <ChakraTab
                  _selected={{
                    backgroundColor: 'primary.100',
                    boxShadow: 'none',
                    color: 'white',
                  }}
                  color="neutral.secondary"
                  id={`${id}__${eachTab
                    .replaceAll(' ', '-')
                    .toLowerCase()}-tab`}
                  key={eachTab}
                  lineHeight="16.8px"
                  padding="6.5px 12px"
                >
                  <ChakraText
                    id={`${id}__${eachTab
                      .replaceAll(' ', '-')
                      .toLowerCase()}-tab-label`}
                    {...labelStyle}
                  >
                    {eachTab}
                  </ChakraText>
                </ChakraTab>
              ))}
            </ChakraTabList>

            <IconButton
              ariaLabel="close"
              icon={
                <Close fill={neutral.secondary} height="24px" width="24px" />
              }
              id={`${id}__close`}
              onClick={closeModal}
              variant="ghost"
            />
          </ChakraHStack>
        </ChakraFlex>

        <ChakraTabPanels height="100%" id={`${id}__tab-panels-container`}>
          <ChakraTabPanel id={`${id}__only-one-tab-panel-container`}>
            <AssessmentsTable
              id={`${id}__only-one-tab-panel`}
              tableData={[clickedSurveyData]}
            />
          </ChakraTabPanel>
          <ChakraTabPanel id={`${id}__last-five-tab-panel-container`}>
            <AssessmentsTable
              id={`${id}__last-five-tab-panel`}
              tableData={lastFiveSurveyData}
            />
          </ChakraTabPanel>
        </ChakraTabPanels>
      </ChakraTabs>
    </ChakraBox>
  );
};

export default AssessmentsView;
